import { useCallback, useEffect, useRef, useState } from 'react';
import { uiSocket } from '../live/socket';
import { useLive } from '../live/store';

/**
 * Remote driving (PROTOCOL §5): `{type:"drive", robotId, v, w}` on /ws/ui every 100 ms while a
 * command is active, and one `v=0,w=0` frame when it ends. Ends automatically on window blur,
 * page hide and unmount. +v = forward (m/s), +w = turn left / CCW (°/s).
 */
export const MAX_V = 1.0;
export const MAX_W = 60;
const PERIOD_MS = 100;

export interface DriveCmd {
  v: number;
  w: number;
}

export function useDriveLoop(serial: string | undefined) {
  const cmdRef = useRef<DriveCmd | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const serialRef = useRef(serial);
  const [active, setActive] = useState<DriveCmd | null>(null);

  const send = useCallback((c: DriveCmd) => {
    const s = serialRef.current;
    if (!s) return;
    uiSocket.send({ type: 'drive', robotId: s, v: round(clamp(c.v, MAX_V), 2), w: Math.round(clamp(c.w, MAX_W)) });
  }, []);

  const set = useCallback(
    (c: DriveCmd | null) => {
      const prev = cmdRef.current;
      cmdRef.current = c;
      if (c && (c.v !== 0 || c.w !== 0)) {
        if (!timer.current) {
          send(c);
          timer.current = setInterval(() => cmdRef.current && send(cmdRef.current), PERIOD_MS);
        }
        setActive(c);
      } else {
        if (timer.current) {
          clearInterval(timer.current);
          timer.current = null;
        }
        if (prev && (prev.v !== 0 || prev.w !== 0)) send({ v: 0, w: 0 });
        cmdRef.current = null;
        setActive(null);
      }
    },
    [send],
  );

  // Serial change: stop driving the previous robot.
  useEffect(() => {
    if (serialRef.current !== serial) {
      set(null);
      serialRef.current = serial;
    }
  }, [serial, set]);

  useEffect(() => {
    const stop = () => set(null);
    const onVis = () => document.hidden && stop();
    window.addEventListener('blur', stop);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('blur', stop);
      document.removeEventListener('visibilitychange', onVis);
      stop();
    };
  }, [set]);

  return { set, active };
}

export type DriveKey = 'fwd' | 'back' | 'left' | 'right';
/** Physical key positions (KeyboardEvent.code), so WASD works in any layout (ЦФЫВ on Russian). */
const KEYMAP: Record<string, DriveKey> = { KeyW: 'fwd', ArrowUp: 'fwd', KeyS: 'back', ArrowDown: 'back', KeyA: 'left', ArrowLeft: 'left', KeyD: 'right', ArrowRight: 'right' };
/** Turn rate used while also driving forward/back: a smooth arc instead of a spin. */
const ARC_TURN_FACTOR = 0.5;

function typingTarget(t: EventTarget | null) {
  const el = t as HTMLElement | null;
  return Boolean(el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable));
}

/**
 * WASD / arrow-key driving + on-screen pad for one robot.
 *
 * The command is derived, never accumulated: held = keyboard keys that are physically down ∪ keys held by on-screen
 * pointers (mouse / each touch). Keyboard keys are tracked by keydown/keyup alone and survive anything that only
 * pauses driving (robot briefly offline, disabled, the loop stopping); only a real focus loss (window blur / hidden
 * tab) forgets them, because their keyup may never arrive. Earlier the held set was cleared on every interruption,
 * and since browsers only auto-repeat the LAST pressed key, the first key (W) was lost for good when the second one
 * (D) came in: the robot got "D alone" at full turn rate instead of a W+D arc.
 */
export function useKeyDrive(serial: string | undefined, opts: { vMax: number; wMax: number; enabled: boolean; onStop: () => void }) {
  const { set } = useDriveLoop(serial);
  const [held, setHeld] = useState<Set<DriveKey>>(new Set());
  const kbd = useRef(new Set<DriveKey>());
  const ptr = useRef(new Map<number, DriveKey[]>());
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });
  const online = useLive((s) => (serial ? Boolean(s.robots[serial]?.online) : false));
  const onlineRef = useRef(online);
  useEffect(() => {
    onlineRef.current = online;
  });
  const lastKey = useRef('');

  /** Recompute the command from the keyboard + pointer state and send it if it changed. */
  const sync = useCallback(
    (force = false) => {
      const can = onlineRef.current && optsRef.current.enabled;
      const h = new Set<DriveKey>(can ? [...kbd.current, ...[...ptr.current.values()].flat()] : []);
      const { vMax, wMax } = optsRef.current;
      const key = `${[...h].sort().join(',')}|${vMax}|${wMax}`;
      if (!force && key === lastKey.current) return;
      lastKey.current = key;
      setHeld(h);
      const v = ((h.has('fwd') ? 1 : 0) - (h.has('back') ? 1 : 0)) * vMax;
      const turn = (h.has('left') ? 1 : 0) - (h.has('right') ? 1 : 0);
      const w = turn * wMax * (v !== 0 ? ARC_TURN_FACTOR : 1);
      set(h.size ? { v, w } : null);
    },
    [set],
  );

  /** On-screen pad: one pointer may hold several keys at once (diagonals), applied atomically. */
  const pointerDown = useCallback(
    (id: number, keys: DriveKey[]) => {
      if (!onlineRef.current || !optsRef.current.enabled) return;
      ptr.current.set(id, keys);
      sync();
    },
    [sync],
  );
  const pointerUp = useCallback(
    (id: number) => {
      if (!ptr.current.delete(id)) return;
      sync();
    },
    [sync],
  );
  /** Single-key helpers kept for callers that press one key (no pointer id). */
  const press = useCallback((k: DriveKey) => pointerDown(-1000 - ['fwd', 'back', 'left', 'right'].indexOf(k), [k]), [pointerDown]);
  const release = useCallback((k: DriveKey) => pointerUp(-1000 - ['fwd', 'back', 'left', 'right'].indexOf(k)), [pointerUp]);
  const releaseAll = useCallback(() => {
    kbd.current.clear();
    ptr.current.clear();
    sync();
  }, [sync]);

  // Speed slider / online / enabled changes re-derive the command (pause while offline or disabled, resume after).
  useEffect(() => {
    sync();
  }, [opts.vMax, opts.wMax, opts.enabled, online, sync]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const k = KEYMAP[e.code];
      if (!k) {
        if ((e.key === ' ' || e.key === 'Escape') && optsRef.current.enabled && !typingTarget(e.target) && !e.metaKey && !e.ctrlKey && !e.altKey && !document.querySelector('[role="dialog"][aria-modal="true"]')) {
          e.preventDefault();
          releaseAll();
          optsRef.current.onStop();
        }
        return;
      }
      if (!optsRef.current.enabled || typingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      e.preventDefault();
      // repeats too: they re-assert a key that is still down (never toggles anything off)
      kbd.current.add(k);
      sync();
    };
    const up = (e: KeyboardEvent) => {
      const k = KEYMAP[e.code];
      if (!k || !kbd.current.delete(k)) return;
      sync();
    };
    // focus loss: keyups can no longer be trusted → forget everything (the loop itself also stops on blur)
    const lost = () => {
      kbd.current.clear();
      ptr.current.clear();
      sync(true);
    };
    const onVis = () => document.hidden && lost();
    document.addEventListener('keydown', down);
    document.addEventListener('keyup', up);
    window.addEventListener('blur', lost);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('keydown', down);
      document.removeEventListener('keyup', up);
      window.removeEventListener('blur', lost);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [sync, releaseAll]);

  // the command being sent (for the status line); same formula as `sync`
  const v = ((held.has('fwd') ? 1 : 0) - (held.has('back') ? 1 : 0)) * opts.vMax;
  const turn = (held.has('left') ? 1 : 0) - (held.has('right') ? 1 : 0);
  const active: DriveCmd | null = held.size && (v !== 0 || turn !== 0) ? { v, w: turn * opts.wMax * (v !== 0 ? ARC_TURN_FACTOR : 1) } : null;
  return { held, press, release, releaseAll, pointerDown, pointerUp, active };
}

function clamp(x: number, m: number) {
  return Math.max(-m, Math.min(m, x));
}
function round(x: number, d: number) {
  const f = 10 ** d;
  return Math.round(x * f) / f;
}
